import React, { useState, useEffect, useRef } from 'react';
import { useConversation } from '@elevenlabs/react';
import * as THREE from 'three';

const VoiceChat = ({ onConversationStateChange, onAudioAnalysis, showButton = true }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioData, setAudioData] = useState({
    volume: 0,
    frequency: 0,
    intensity: 0,
    speechRate: 0
  });

  const simulationIntervalRef = useRef();
  const speakingTimeoutRef = useRef();
  const fadeOutStartTimeRef = useRef(null);

  const conversation = useConversation({
    agentId: 'agent_01jy9egkfrex78eh9fehd0fkbc',
    onConnect: () => {
      console.log('✅ Connected to ElevenLabs');
      setIsConnected(true);
      onConversationStateChange?.('connected');
    },
    onDisconnect: () => {
      console.log('❌ Disconnected from ElevenLabs');
      setIsConnected(false);
      setIsSpeaking(false);
      onConversationStateChange?.('idle');
      stopAudioSimulation();
    },
    onMessage: (message) => {
      console.log('💬 Message:', message);
      // Now using built-in isSpeaking state instead of message detection
    },
    onError: (error) => {
      console.error('🚨 Conversation error:', error);
      onConversationStateChange?.('error');
    }
  });

  // Use the built-in isSpeaking state for immediate response!
  const isAgentSpeaking = conversation.isSpeaking;
  
  // React immediately to isSpeaking changes
  useEffect(() => {
    console.log('🎯 Built-in isSpeaking changed:', isAgentSpeaking);
    
    if (isAgentSpeaking) {
      console.log('🎵 IMMEDIATE: Agent speaking detected via built-in state - starting simulation NOW');
      // Clear any existing fade-out timeout and reset fade-out timer
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
      fadeOutStartTimeRef.current = null; // Reset fade-out
      setIsSpeaking(true);
      onConversationStateChange?.('speaking');
      startAudioSimulation();
    } else {
      console.log('🔇 Agent stopped speaking - starting graceful fade-out');
      setIsSpeaking(false);
      onConversationStateChange?.('listening');
      fadeOutStartTimeRef.current = Date.now(); // Start fade-out timer
      
      // Instead of stopping immediately, fade out over 1.5 seconds
      speakingTimeoutRef.current = setTimeout(() => {
        console.log('🌅 Graceful fade-out complete - stopping simulation');
        stopAudioSimulation();
        fadeOutStartTimeRef.current = null;
      }, 1500); // 1.5 second fade-out
    }
  }, [isAgentSpeaking]);

  const startAudioSimulation = () => {
    console.log('🎵 Starting AI audio simulation');
    
    // Clear any existing simulation
    if (simulationIntervalRef.current) {
      clearInterval(simulationIntervalRef.current);
    }
    
    let startTime = Date.now();
    let isSimulationActive = true; // Local flag to control simulation
    
    const simulate = () => {
      // Check both the local flag and the state
      if (!isSimulationActive) {
        console.log('🛑 Stopping simulation - local flag disabled');
        return;
      }
      
      const elapsed = (Date.now() - startTime) / 1000; // seconds
      
      // More subtle volume simulation that varies over time
      let volume = 0.15 + 
        Math.sin(elapsed * 3) * 0.08 + 
        Math.sin(elapsed * 7) * 0.06 + 
        Math.sin(elapsed * 1.5) * 0.04;
      
      // Gentler frequency simulation (simulates voice pitch/depth changes)
      // Lower frequency = higher pitch, Higher frequency = deeper voice
      // Range: 0.8 to 1.4 (maps to curl boost of 0.08 to 0.14) - much more subtle
      let frequency = 0.2 + 
        Math.sin(elapsed * 2.1) * 0.15 +     // Slow pitch variations (reduced)
        Math.sin(elapsed * 5.3) * 0.08 +     // Medium pitch variations (reduced)
        Math.sin(elapsed * 0.7) * 0.12;      // Very slow pitch drifts (reduced)
      
      frequency = Math.max(0.8, Math.min(1.4, frequency)); // Clamp to much narrower range
      
      // Apply fade-out if agent stopped speaking but we're still in grace period
      if (!isAgentSpeaking && fadeOutStartTimeRef.current) {
        // Calculate fade-out multiplier (1.0 to 0.0 over 1.5 seconds)
        const fadeOutDuration = 1.5; // seconds
        const timeSinceStop = (Date.now() - fadeOutStartTimeRef.current) / 1000;
        const fadeMultiplier = Math.max(0, 1 - (timeSinceStop / fadeOutDuration));
        volume *= fadeMultiplier;
        // Frequency also returns to neutral during fade-out
        frequency = THREE.MathUtils.lerp(frequency, 1.5, 1 - fadeMultiplier);
        console.log('🌅 Fading out:', { timeSinceStop: timeSinceStop.toFixed(2), fadeMultiplier: fadeMultiplier.toFixed(2), volume: volume.toFixed(2), frequency: frequency.toFixed(2) });
      }
      
      const newAudioData = {
        volume: Math.max(0.05, Math.min(1, volume)), // Lower minimum for fade-out
        frequency: frequency,
        intensity: 0.5,
        speechRate: 0.7
      };

      console.log('🎤 AI Audio:', { 
        volume: newAudioData.volume.toFixed(2), 
        frequency: newAudioData.frequency.toFixed(2), 
        elapsed: elapsed.toFixed(1) 
      });
      setAudioData(newAudioData);
      onAudioAnalysis?.(newAudioData);
    };
    
    // Store the stop function
    const stopSimulation = () => {
      isSimulationActive = false;
      if (simulationIntervalRef.current) {
        clearInterval(simulationIntervalRef.current);
        simulationIntervalRef.current = null;
      }
    };
    
    // Start the interval - reduced to 25ms for immediate response
    simulationIntervalRef.current = setInterval(simulate, 25);
    simulate(); // Run immediately
    
    // Store stop function for cleanup
    simulationIntervalRef.stopFunction = stopSimulation;
  };

  const stopAudioSimulation = () => {
    console.log('🔇 Stopping audio simulation');
    
    // Call the stop function if it exists
    if (simulationIntervalRef.stopFunction) {
      simulationIntervalRef.stopFunction();
    }
    
    // Also clear the interval directly
    if (simulationIntervalRef.current) {
      clearInterval(simulationIntervalRef.current);
      simulationIntervalRef.current = null;
    }
    
    const silentData = { volume: 0, frequency: 0, intensity: 0, speechRate: 0 };
    setAudioData(silentData);
    onAudioAnalysis?.(silentData);
  };

  const handleToggleConversation = async () => {
    try {
      if (isConnected) {
        console.log('🛑 Ending conversation...');
        await conversation.endSession();
      } else {
        console.log('🚀 Starting conversation...');
        await conversation.startSession();
      }
    } catch (error) {
      console.error('❌ Failed to toggle conversation:', error);
    }
  };

   // Add keyboard event listener for spacebar
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if spacebar was pressed (keyCode 32 or event.code === 'Space')
      if (event.code === 'Space' || event.key === ' ') {
        // Prevent default behavior (scrolling)
        event.preventDefault();
        
        // Only trigger if not currently connecting AND button is visible
        if (conversation.status !== 'connecting' && showButton) {
          console.log('⭐ Spacebar pressed - toggling conversation');
          handleToggleConversation();
        }
      }
    };

    // Add event listener to window
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isConnected, conversation.status, showButton]); // Added showButton to dependencies

  useEffect(() => {
    return () => {
      stopAudioSimulation();
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
      fadeOutStartTimeRef.current = null;
    };
  }, []);

  // Render button icon based on state
  const renderButtonIcon = () => {
    if (conversation.status === 'connecting') {
      // Simple loading dots
      return (
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          <div style={{ width: '3px', height: '3px', backgroundColor: 'white', borderRadius: '50%', animation: 'pulse 1.5s infinite' }}></div>
          <div style={{ width: '3px', height: '3px', backgroundColor: 'white', borderRadius: '50%', animation: 'pulse 1.5s infinite 0.5s' }}></div>
          <div style={{ width: '3px', height: '3px', backgroundColor: 'white', borderRadius: '50%', animation: 'pulse 1.5s infinite 1s' }}></div>
        </div>
      );
    } else if (isConnected) {
      // Stop button (square)
      return (
        <div style={{
          width: '14px',
          height: '14px',
          border: '2.5px solid white',
          backgroundColor: 'transparent'
        }}></div>
      );
    } else {
      // Play button (pointier triangle with beveled edges)
      return (
        <svg width="18" height="18" viewBox="0 0 16 16" style={{ marginLeft: '2px' }}>
          <polygon 
            points="3,2 3,14 15,8" 
            fill="none" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
        `}
      </style>
      {showButton && (
        <div style={{
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000
        }}>
          <button
            onClick={handleToggleConversation}
            disabled={conversation.status === 'connecting'}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: '2px solid white',
              background: 'transparent',
              color: 'white',
              cursor: conversation.status === 'connecting' ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.2s ease',
              opacity: conversation.status === 'connecting' ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              if (conversation.status !== 'connecting') {
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
            }}
          >
            {renderButtonIcon()}
          </button>
        </div>
      )}
    </>
  );
};

export default VoiceChat; 